import type { PublicCatalogueStatus } from "../../../types/catalogue.types";

/**
 * Who is looking at a shared catalogue. One buyer page serves all of them; the shell (header, price
 * wording, actions) changes per audience while pieces, photos and the making story stay the same.
 *
 * - studio:   the studio previewing its own catalogue
 * - direct:   the studio's own buyer (today's experience)
 * - reseller: a trade buyer who may forward the collection to their own clients
 * - client:   a reseller's client; never sees the studio
 */
export type CatalogueAudience = "studio" | "direct" | "reseller" | "client";

export function audienceFromStatus(meta?: PublicCatalogueStatus | null): CatalogueAudience {
  if (!meta) return "direct";
  if (meta.studioPreview) return "studio";
  if (!meta.resellerSharing) return "direct";
  if (meta.linkRole === "CLIENT") return "client";
  if (meta.linkRole === "RESELLER") return "reseller";
  return "direct";
}

export interface AudienceChrome {
  /** Name in the header. */
  hostName: string;
  subtitle: string;
  pill: string;
  /** Text inside the square mark, or null for a neutral mark (no studio initials). */
  mark: string | null;
  footer: string;
}

const PLATFORM_FOOTER = "HastKala Pramanak — authenticity and traceability for handloom and handicraft.";

export function chromeFor(
  audience: CatalogueAudience,
  meta: PublicCatalogueStatus | null | undefined,
  loading: boolean
): AudienceChrome {
  if (audience === "client") {
    const curator = meta?.curatorName?.trim();
    return {
      hostName: curator || "Curated for you",
      subtitle: curator ? "Curated for you" : "Handpicked pieces",
      pill: "Private link",
      mark: curator ? initialsOf(curator) : null,
      footer: "Made by hand. Each piece's story is recorded on HastKala Pramanak.",
    };
  }
  const host = meta?.organizationName || (loading ? "…" : "Maker");
  if (audience === "reseller") {
    return {
      hostName: host,
      subtitle: "Trade collection for you",
      pill: "Reseller",
      mark: "HK",
      footer: PLATFORM_FOOTER,
    };
  }
  return {
    hostName: host,
    subtitle: "Private collection for you",
    pill: meta?.requirePassword ? "Protected collection" : "Private link",
    mark: "HK",
    footer: PLATFORM_FOOTER,
  };
}

/** Up to two letters from the first and last word, by grapheme so names like "Ö" or emoji stay whole. */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  const first = firstGrapheme(words[0]);
  const last = words.length > 1 ? firstGrapheme(words[words.length - 1]) : "";
  return (first + last).toUpperCase();
}

function firstGrapheme(word: string): string {
  const Seg = (Intl as any).Segmenter;
  if (Seg) {
    const it = new Seg(undefined, { granularity: "grapheme" }).segment(word)[Symbol.iterator]();
    const next = it.next();
    return next.done ? "" : next.value.segment;
  }
  return Array.from(word)[0] || "";
}

/** Who the buyer should contact, in sentences like "Ask {host} for a new link." */
export function hostPhrase(audience: CatalogueAudience, meta?: PublicCatalogueStatus | null): string {
  if (audience === "client") {
    return meta?.curatorName?.trim() || "the person who shared this";
  }
  return "the studio";
}

/** Same rounding as the server: per-piece price wins, otherwise markup on the reseller's price. */
export function clientPricePreview(
  resellerPrice: number | null | undefined,
  markupPercent: number | null | undefined,
  override?: number | null
): number | null {
  if (override != null && override > 0) return Math.round(override);
  if (resellerPrice == null || !(resellerPrice > 0)) return null;
  return Math.round(resellerPrice * (1 + (markupPercent || 0) / 100));
}

export function parsePrice(raw: unknown): number | null {
  if (typeof raw === "number") return raw > 0 ? raw : null;
  if (typeof raw !== "string" || !raw.trim()) return null;
  const n = Number(raw.replace(/[,₹\s]/g, ""));
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function formatRupees(n: number): string {
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n)}`;
}

export function sharesLeftLabel(remaining: number, limit: number): string {
  if (limit <= 0) return "";
  if (remaining <= 0) return `All ${limit} shares used`;
  return `${remaining} of ${limit} share${limit === 1 ? "" : "s"} left`;
}

/** Message the reseller sends their client. Hindi first, then English, in the reseller's name. */
export function clientInviteMessage(opts: {
  clientName: string;
  resellerName?: string;
  catalogueName?: string;
  url: string;
  /** Open link: no number needed to open it. */
  open?: boolean;
}): string {
  const client = opts.clientName.trim() || "ji";
  const from = opts.resellerName?.trim();
  const collection = opts.catalogueName?.trim();
  const hi = [
    `नमस्ते ${client} जी,`,
    collection
      ? `आपके लिए "${collection}" से कुछ हस्तनिर्मित पीस चुने हैं।`
      : "आपके लिए कुछ हस्तनिर्मित पीस चुने हैं।",
    opts.open ? "नीचे दिया लिंक सीधे खुल जाएगा।" : "नीचे दिया लिंक सिर्फ़ आपके मोबाइल नंबर से खुलेगा।",
  ].join(" ");
  const en = [
    `Hello ${opts.clientName.trim() || "there"},`,
    collection ? `I've picked some handmade pieces from "${collection}" for you.` : "I've picked some handmade pieces for you.",
    opts.open ? "Tap the link below to open it." : "The link below opens with your mobile number.",
  ].join(" ");
  const sign = from ? `\n\n— ${from}` : "";
  return `${hi}\n\n${en}\n\n${opts.url}${sign}`;
}

/**
 * wa.me needs the full international number. Stored phones are 10-digit Indian or international
 * digits; when the link's country code is known it decides, so a 10-digit foreign number is not
 * mistaken for Indian and a local number gets its dial code.
 */
export function waDigits(phone: string | null | undefined, countryCode?: string | null): string {
  let digits = String(phone || "").replace(/\D/g, "");
  const dial = String(countryCode || "").replace(/\D/g, "");
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (dial) {
    if (digits.startsWith(dial) && digits.length > dial.length + 6) return digits;
    return `${dial}${digits.replace(/^0+/, "")}`;
  }
  return digits.length === 10 ? `91${digits}` : digits;
}

export function whatsAppHref(phoneDigits: string | null | undefined, text: string): string {
  const digits = String(phoneDigits || "").replace(/\D/g, "");
  const base = digits ? `https://wa.me/${digits}` : "https://wa.me/";
  return `${base}?text=${encodeURIComponent(text)}`;
}

/**
 * A buyer asks about one piece. The piece link goes first so WhatsApp can preview it, then the
 * photo so staff can tell the piece at a glance. Never an internal id: it means nothing to a person.
 */
export function askAboutPieceMessage(
  pieceName: string,
  links: { pieceUrl?: string; photoUrl?: string } = {}
): string {
  const piece = pieceName.trim() || "this piece";
  const lines = [
    `नमस्ते, मुझे "${piece}" के बारे में जानना है।`,
    `Hello, I'd like to know more about "${piece}".`,
  ];
  if (links.pieceUrl) lines.push("", links.pieceUrl);
  if (links.photoUrl) lines.push("", `फ़ोटो / Photo: ${links.photoUrl}`);
  return lines.join("\n");
}
